import {
  CIPHER_MP_FONT_FACES_CSS,
  parseFontFaceRulesFromCss,
  rewriteFontCssUrls,
  resolveFontFaceUrl,
  type ParsedFontFaceRule,
} from '@cipher-display/cipherFontCdn';
import { cipherFontFamilyForCodepoint } from '@cipher-display/cipherFonts';
import {
  isCipherFontDebugEnabled,
  getCipherFontLoadReport,
  recordCipherFontLoadFailure,
  recordCipherFontLoadSummary,
  clearCipherFontLoadLogs,
} from '@cipher-display/cipherFontLoadLog';
import {
  codepointInUnicodeRange,
  uniqueCodepointsFromText,
} from '@cipher-display/cipherUnicodeRange';
import { getCipherFontCdnBase } from '@/config';

let appliedCdnBase: string | null = null;
let registryPromise: Promise<void> | null = null;
let fontRules: ParsedFontFaceRule[] = [];
const loadedFaceKeys = new Set<string>();
const loadingFaceKeys = new Map<string, Promise<boolean>>();
const LOAD_FONT_FACE_CONCURRENCY = 24;

function faceKey(rule: ParsedFontFaceRule): string {
  return `${rule.family}\0${rule.sourceUrl}`;
}

function normalizeFamily(name: string): string {
  return name.replace(/^["']|["']$/g, '');
}

/** DevTools 调试（仅 development 构建）：getApp().globalData.__cipherFontLoadReport() */
export function exposeCipherFontLoadReportForMp(): void {
  if (!isCipherFontDebugEnabled()) return;
  try {
    const app = getApp<{ globalData?: Record<string, unknown> }>();
    if (!app.globalData) app.globalData = {};
    app.globalData.__cipherFontLoadReport = getCipherFontLoadReport;
    app.globalData.__cipherFontLoadClear = clearCipherFontLoadLogs;
  } catch {
    // getApp 在 App 未就绪时可能不可用
  }
}

/**
 * 启动时仅拉取 cipher-mp-font-faces.css 并建立索引，不预载全部 woff2。
 * 实际加载在 CipherText 渲染时按文本码点按需进行。
 */
export function applyCipherFonts(force = false): Promise<void> {
  const cdnBase = getCipherFontCdnBase();
  if (!force && appliedCdnBase === cdnBase && fontRules.length > 0) {
    return Promise.resolve();
  }

  if (registryPromise && !force) {
    return registryPromise;
  }

  registryPromise = initFontRegistry(cdnBase, force)
    .then(() => {
      appliedCdnBase = cdnBase;
      exposeCipherFontLoadReportForMp();
    })
    .catch((err) => {
      recordCipherFontLoadFailure({
        kind: 'css_fetch',
        platform: 'mp',
        message: 'Cipher font CSS load failed',
        url: `${cdnBase.replace(/\/$/, '')}/${CIPHER_MP_FONT_FACES_CSS}`,
        detail: err instanceof Error ? err.message : err,
      });
      recordCipherFontLoadSummary({
        platform: 'mp',
        cdnBase,
        totalFaces: 0,
        failedFaces: 1,
      });
    })
    .finally(() => {
      registryPromise = null;
    });

  return registryPromise;
}

async function initFontRegistry(cdnBase: string, force: boolean): Promise<void> {
  if (force) {
    fontRules = [];
    loadedFaceKeys.clear();
    loadingFaceKeys.clear();
  }

  const cssUrl = `${cdnBase.replace(/\/$/, '')}/${CIPHER_MP_FONT_FACES_CSS}`;
  const cssText = await fetchCss(cssUrl);
  const css = rewriteFontCssUrls(cssText, cdnBase);
  const rules = parseFontFaceRulesFromCss(css);

  if (rules.length === 0) {
    throw new Error(`No @font-face rules in ${cssUrl}`);
  }

  fontRules = rules;

  if (isCipherFontDebugEnabled()) {
    recordCipherFontLoadSummary({
      platform: 'mp',
      cdnBase,
      totalFaces: rules.length,
      failedFaces: 0,
    });
    console.info(
      `[cipher-font][mp] registry ready: ${rules.length} @font-face rules (lazy load on render)`,
    );
  }
}

function rulesForCodepoint(cp: number): ParsedFontFaceRule[] {
  const family = normalizeFamily(cipherFontFamilyForCodepoint(cp));
  return fontRules.filter(
    (rule) =>
      rule.family === family && codepointInUnicodeRange(cp, rule.unicodeRange),
  );
}

function collectRulesForText(text: string): ParsedFontFaceRule[] {
  const pending: ParsedFontFaceRule[] = [];
  const seen = new Set<string>();

  for (const cp of uniqueCodepointsFromText(text)) {
    for (const rule of rulesForCodepoint(cp)) {
      const key = faceKey(rule);
      if (loadedFaceKeys.has(key) || loadingFaceKeys.has(key) || seen.has(key)) {
        continue;
      }
      seen.add(key);
      pending.push(rule);
    }
  }

  return pending;
}

/** 按文本中出现的码点，按需 loadFontFace（同一分片只加载一次） */
export async function ensureCipherFontsForText(text: string): Promise<void> {
  if (!text) return;

  const cdnBase = getCipherFontCdnBase();
  await applyCipherFonts();
  if (fontRules.length === 0) {
    if (isCipherFontDebugEnabled()) {
      console.warn('[cipher-font][mp] font registry empty; CSS fetch may have failed');
    }
    return;
  }

  const codepoints = uniqueCodepointsFromText(text);
  const pending = collectRulesForText(text);

  if (pending.length === 0) {
    if (isCipherFontDebugEnabled()) {
      const sampleCp = codepoints[0];
      const sampleFamily = sampleCp !== undefined
        ? normalizeFamily(cipherFontFamilyForCodepoint(sampleCp))
        : '';
      const matched = sampleCp !== undefined ? rulesForCodepoint(sampleCp).length : 0;
      console.warn(
        '[cipher-font][mp] no pending font faces;',
        `${codepoints.length} codepoints,`,
        sampleCp !== undefined
          ? `sample U+${sampleCp.toString(16).toUpperCase()} → ${sampleFamily} (${matched} rules, ${loadedFaceKeys.size} already loaded)`
          : 'empty text',
      );
    }
    return;
  }

  if (isCipherFontDebugEnabled()) {
    console.info(
      `[cipher-font][mp] downloading ${pending.length} font face(s) for ${codepoints.length} codepoint(s)`,
      pending.slice(0, 3).map((r) => resolveFontFaceUrl(r.sourceUrl, cdnBase)),
    );
  }

  let failed = 0;
  await loadFontFacesWithConcurrency(pending, LOAD_FONT_FACE_CONCURRENCY, cdnBase, (ok) => {
    if (!ok) failed++;
  });

  if (isCipherFontDebugEnabled()) {
    console.info(
      `[cipher-font][mp] lazy loaded ${pending.length - failed}/${pending.length} faces for ${codepoints.length} codepoints`,
    );
  }
}

function isMpDevtools(): boolean {
  try {
    return uni.getSystemInfoSync().platform === 'devtools';
  } catch {
    return false;
  }
}

function loadOneFontFace(rule: ParsedFontFaceRule, cdnBase: string): Promise<boolean> {
  const key = faceKey(rule);
  const inflight = loadingFaceKeys.get(key);
  if (inflight) return inflight;
  if (loadedFaceKeys.has(key)) return Promise.resolve(true);

  const fontUrl = resolveFontFaceUrl(rule.sourceUrl, cdnBase);

  const promise = downloadAndRegisterFontFace(rule.family, fontUrl).then((ok) => {
    loadingFaceKeys.delete(key);
    if (ok) loadedFaceKeys.add(key);
    return ok;
  });

  loadingFaceKeys.set(key, promise);
  return promise;
}

function registerFontFace(
  family: string,
  source: string,
  fontUrl: string,
  via: 'remote' | 'temp',
): Promise<boolean> {
  return new Promise((resolve) => {
    uni.loadFontFace({
      family,
      global: true,
      desc: {
        style: 'normal',
        weight: 'normal',
        variant: 'normal',
      },
      source,
      success: () => {
        if (isCipherFontDebugEnabled()) {
          console.info(`[cipher-font][mp] loadFontFace ok (${via}): ${family}`, fontUrl);
        }
        resolve(true);
      },
      fail: (err) => {
        recordCipherFontLoadFailure({
          kind: 'load_font_face',
          platform: 'mp',
          message: `uni.loadFontFace failed (${via})`,
          family,
          url: fontUrl,
          detail: err,
        });
        resolve(false);
      },
    });
  });
}

/**
 * 真机：先 downloadFile 再 loadFontFace（临时路径在真机更稳定）。
 * 开发者工具：临时路径 loadFontFace 常失败，直接用 HTTPS URL；失败时再走 download 回退。
 */
async function downloadAndRegisterFontFace(family: string, fontUrl: string): Promise<boolean> {
  const remoteSource = `url("${fontUrl}")`;

  if (isMpDevtools()) {
    if (await registerFontFace(family, remoteSource, fontUrl, 'remote')) {
      return true;
    }
  }

  return new Promise((resolve) => {
    uni.downloadFile({
      url: fontUrl,
      success: async (res) => {
        if (res.statusCode !== 200 || !res.tempFilePath) {
          recordCipherFontLoadFailure({
            kind: 'woff2_load',
            platform: 'mp',
            message: 'Font download failed',
            family,
            url: fontUrl,
            detail: { statusCode: res.statusCode },
          });
          resolve(await registerFontFace(family, remoteSource, fontUrl, 'remote'));
          return;
        }

        const tempSource = `url("${res.tempFilePath}")`;
        if (await registerFontFace(family, tempSource, fontUrl, 'temp')) {
          resolve(true);
          return;
        }

        resolve(await registerFontFace(family, remoteSource, fontUrl, 'remote'));
      },
      fail: async (err) => {
        recordCipherFontLoadFailure({
          kind: 'woff2_load',
          platform: 'mp',
          message: 'uni.downloadFile failed',
          family,
          url: fontUrl,
          detail: err,
        });
        resolve(await registerFontFace(family, remoteSource, fontUrl, 'remote'));
      },
    });
  });
}

async function loadFontFacesWithConcurrency(
  rules: ParsedFontFaceRule[],
  concurrency: number,
  cdnBase: string,
  onResult?: (ok: boolean) => void,
): Promise<void> {
  let nextIndex = 0;
  const workerCount = Math.min(concurrency, rules.length);

  async function worker() {
    while (nextIndex < rules.length) {
      const index = nextIndex++;
      const ok = await loadOneFontFace(rules[index], cdnBase);
      onResult?.(ok);
    }
  }

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
}

function fetchCss(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    uni.request({
      url,
      method: 'GET',
      success: (res) => {
        if (res.statusCode !== 200 || typeof res.data !== 'string') {
          reject(new Error(`HTTP ${res.statusCode} fetching ${url}`));
          return;
        }
        resolve(res.data);
      },
      fail: (err) => reject(err),
    });
  });
}

/** 开发模式切换后强制刷新字体 CDN */
export function reloadCipherFonts(): Promise<void> {
  appliedCdnBase = null;
  fontRules = [];
  loadedFaceKeys.clear();
  loadingFaceKeys.clear();
  return applyCipherFonts(true);
}

export { getCipherFontLoadReport };
